import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GitHubIcon from '@mui/icons-material/GitHub';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import { useApiClient } from '../../config/axios';
import { useCustomer } from '../../contexts/CustomerContext';
import { useAuthContext } from '../../Auth';
import PipelineOutput from './PipelineOutput';

interface CustomerPipelineProps {
  addLogEntry: (level: 'info' | 'success' | 'error' | 'warning', message: string) => void;
}

interface OciSyncRow {
  tenant: string;
  environment: string;
  bucket_name: string;
  last_bootstrap_at: string | null;
  last_bootstrap_fingerprint: string | null;
  last_publish_at: string | null;
  last_publish_fingerprint: string | null;
}

interface GithubSyncRow {
  tenant: string;
  github_url: string;
  branch: string | null;
  subdir: string | null;
  last_ingested_at: string | null;
  last_ingested_sha: string | null;
  last_ingested_by: string | null;
}

interface GithubForm {
  github_url: string;
  branch: string;
  subdir: string;
  username: string;
  password: string;
}

interface OnboardForm {
  github_url: string;
  branch: string;
  subdir: string;
  oci_object_prefix: string;
  username: string;
  password: string;
}

const EMPTY_GH_FORM: GithubForm = {
  github_url: '',
  branch: 'main',
  subdir: '',
  username: '',
  password: '',
};

const EMPTY_ONBOARD_FORM: OnboardForm = {
  github_url: '',
  branch: 'main',
  subdir: '',
  oci_object_prefix: '',
  username: '',
  password: '',
};

// Shared onboard-dialog renderer — used from both the no-customer branch
// (bootstrap onboarding) and the normal branch (add another customer).
// Kept module-scoped so the JSX doesn't diverge between call sites.
const onboardDialog = (
  form: OnboardForm,
  setForm: React.Dispatch<React.SetStateAction<OnboardForm>>,
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  onStart: () => void,
  valid: boolean
) => (
  <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
    <DialogTitle>Onboard New Customer from GitHub</DialogTitle>
    <DialogContent>
      <Alert severity="info" sx={{ mb: 2 }}>
        The tenant slug + customer name are read from <code>meta/M_Meta.conf</code> in the repo —
        no manual entry needed. On success this becomes the currently-selected customer at
        <code>(tenant, repo)</code>.
      </Alert>
      <Stack spacing={2} sx={{ mt: 1 }}>
        <TextField
          label="Repository URL (HTTPS)"
          placeholder="https://github.com/s5-stratos/trd-configs.git"
          value={form.github_url}
          onChange={e => setForm(f => ({ ...f, github_url: e.target.value }))}
          required
          fullWidth
        />
        <Stack direction="row" spacing={2}>
          <TextField
            label="Branch"
            value={form.branch}
            onChange={e => setForm(f => ({ ...f, branch: e.target.value }))}
            required
            sx={{ flex: 1 }}
          />
          <TextField
            label="Subdirectory (optional)"
            placeholder="e.g. configuration/"
            value={form.subdir}
            onChange={e => setForm(f => ({ ...f, subdir: e.target.value }))}
            sx={{ flex: 1 }}
          />
        </Stack>
        <TextField
          label="OCI Object Prefix (optional)"
          placeholder="defaults to tenant slug from M_Meta.conf (e.g. 'belk' for tenant 'blk')"
          value={form.oci_object_prefix}
          onChange={e => setForm(f => ({ ...f, oci_object_prefix: e.target.value }))}
          helperText="Only set if the OCI bucket path differs from the tenant slug. This is stored once and reused for every OCI load."
          fullWidth
        />
        <TextField
          label="GitHub Username"
          value={form.username}
          onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
          required
          autoComplete="off"
          fullWidth
        />
        <TextField
          label="Personal Access Token (PAT)"
          type="password"
          value={form.password}
          onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
          required
          autoComplete="off"
          helperText="Sent once with this request; never stored. Needs 'repo' scope on the target repo."
          fullWidth
        />
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={() => setOpen(false)}>Cancel</Button>
      <Button variant="contained" onClick={onStart} disabled={!valid}>
        Onboard
      </Button>
    </DialogActions>
  </Dialog>
);

const formatRelative = (iso: string | null): string => {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const CustomerPipeline: React.FC<CustomerPipelineProps> = ({ addLogEntry }) => {
  const { currentCustomer, loadCustomers, setCustomer } = useCustomer();
  const { tokenData } = useAuthContext();
  const apiClient = useApiClient();

  const [ociSync, setOciSync] = useState<OciSyncRow | null>(null);
  const [ociLoadRunning, setOciLoadRunning] = useState(false);
  const [ociDialogOpen, setOciDialogOpen] = useState(false);
  const [ociBucket, setOciBucket] = useState<string>('');
  const [publishRunning, setPublishRunning] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishDryRun, setPublishDryRun] = useState(true);

  const [ghSync, setGhSync] = useState<GithubSyncRow | null>(null);
  const [ghLoadRunning, setGhLoadRunning] = useState(false);
  const [ghDialogOpen, setGhDialogOpen] = useState(false);
  const [ghForm, setGhForm] = useState<GithubForm>(EMPTY_GH_FORM);

  const [onboardRunning, setOnboardRunning] = useState(false);
  const [onboardDialogOpen, setOnboardDialogOpen] = useState(false);
  const [onboardForm, setOnboardForm] = useState<OnboardForm>(EMPTY_ONBOARD_FORM);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Which flow's SSE panel is currently active. Only one can run at a
  // time, so a single output panel is enough — this tells it which
  // endpoint to listen on.
  const activeFlow: 'oci-load' | 'oci-publish' | 'gh-load' | 'onboard' =
    onboardRunning ? 'onboard'
      : publishRunning ? 'oci-publish'
        : ghLoadRunning ? 'gh-load'
        : 'oci-load';
  const anyFlowRunning = onboardRunning || publishRunning || ghLoadRunning || ociLoadRunning;

  // Load actions have inherent target envs (OCI → qa, GitHub → repo);
  // both are usable regardless of what env is selected in the header.
  // Fetch BOTH sync statuses so both cards can show 'Last loaded'.
  //
  // Fetching OCI sync always targets env='qa' (the only OCI-wired env
  // today). If dev/prod come online, this becomes a per-env fetch.
  useEffect(() => {
    if (!currentCustomer) return;
    apiClient
      .get('/pipeline/oci-sync', { headers: { 'X-Environment': 'qa' } })
      .then(r => setOciSync(r.data))
      .catch(() => setOciSync(null));
    apiClient
      .get('/pipeline/github-sync')
      .then(r => setGhSync(r.data))
      .catch(() => setGhSync(null));
  }, [apiClient, currentCustomer, refreshTrigger]);

  // Pre-fill the GitHub dialog with last-used URL/branch/subdir when the
  // dialog opens (credentials NEVER pre-fill — always freshly entered).
  useEffect(() => {
    if (ghDialogOpen) {
      setGhForm({
        github_url: ghSync?.github_url || '',
        branch: ghSync?.branch || 'main',
        subdir: ghSync?.subdir || '',
        username: ghSync?.last_ingested_by || '',
        password: '',
      });
    }
  }, [ghDialogOpen, ghSync]);

  const handleOciLoadComplete = useCallback(
    (code: number) => {
      setOciLoadRunning(false);
      addLogEntry(
        code === 0 ? 'success' : 'error',
        `Load from OCI ${code === 0 ? 'completed' : `failed (exit ${code})`}`
      );
      if (code === 0) setRefreshTrigger(x => x + 1);
    },
    [addLogEntry]
  );

  const handlePublishComplete = useCallback(
    (code: number) => {
      setPublishRunning(false);
      addLogEntry(
        code === 0 ? 'success' : 'error',
        `Publish to OCI ${code === 0 ? 'completed' : `failed (exit ${code})`}${publishDryRun ? ' (dry-run)' : ''}`
      );
      if (code === 0 && !publishDryRun) setRefreshTrigger(x => x + 1);
    },
    [addLogEntry, publishDryRun]
  );

  const startPublish = () => {
    setPublishDialogOpen(false);
    setPublishRunning(true);
  };

  const startOciLoad = () => {
    setOciDialogOpen(false);
    setOciLoadRunning(true);
  };
  const ociBucketValid = /^[a-zA-Z0-9._-]+$/.test(ociBucket.trim());

  const handleGhLoadComplete = useCallback(
    (code: number) => {
      setGhLoadRunning(false);
      // Scrub the password from memory. URL/branch/subdir stay so a
      // subsequent open of the modal is pre-filled (they're not sensitive).
      setGhForm(f => ({ ...f, password: '' }));
      addLogEntry(
        code === 0 ? 'success' : 'error',
        `Load from GitHub ${code === 0 ? 'completed' : `failed (exit ${code})`}`
      );
      if (code === 0) setRefreshTrigger(x => x + 1);
    },
    [addLogEntry]
  );

  const startGhLoad = () => {
    setGhDialogOpen(false);
    setGhLoadRunning(true);
  };

  const handleOnboardComplete = useCallback(
    (code: number, payload?: Record<string, unknown>) => {
      setOnboardRunning(false);
      setOnboardForm(f => ({ ...f, password: '' }));
      if (code !== 0) {
        addLogEntry('error', `Onboard from GitHub failed (exit ${code})`);
        return;
      }
      const tenant = payload?.tenant as string | undefined;
      const name = payload?.customer_name as string | undefined;
      addLogEntry('success', `Onboarded ${name ?? tenant ?? 'new customer'} — refreshing picker and switching context`);
      // Refresh the picker's available list, then auto-switch to the newly
      // onboarded customer at env='repo'. setCustomer reloads the page so
      // the whole app picks up the new tenant/env context.
      loadCustomers()
        .then(() => {
          if (tenant && tokenData?.email) {
            return setCustomer(tenant, 'repo', tokenData.email);
          }
          return undefined;
        })
        .catch(err => {
          console.error('Post-onboard switch failed:', err);
          addLogEntry('error', `Auto-select failed: ${err.message}. Refresh and pick from the header.`);
        });
    },
    [addLogEntry, loadCustomers, setCustomer, tokenData]
  );

  const startOnboard = () => {
    setOnboardDialogOpen(false);
    setOnboardRunning(true);
  };

  const onboardFormValid = Boolean(
    onboardForm.github_url.startsWith('https://') &&
    onboardForm.branch.trim() &&
    onboardForm.username.trim() &&
    onboardForm.password.trim() &&
    (!onboardForm.oci_object_prefix || /^[a-z0-9_-]+$/i.test(onboardForm.oci_object_prefix))
  );

  // The onboard button is available in both branches (no-customer and
  // has-customer) — it's how the first-ever customer gets bootstrapped.
  const onboardButton = (
    <Button
      variant="contained"
      color="secondary"
      startIcon={onboardRunning ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
      disabled={anyFlowRunning}
      onClick={() => { setOnboardForm(EMPTY_ONBOARD_FORM); setOnboardDialogOpen(true); }}
    >
      {onboardRunning ? 'Onboarding…' : 'Onboard New Customer'}
    </Button>
  );

  if (!currentCustomer) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          No tenant selected. Onboard a new customer from their GitHub config repo, or select an
          existing tenant from the header.
        </Alert>
        <Box sx={{ mb: 3 }}>{onboardButton}</Box>
        {activeFlow === 'onboard' && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Onboarding Output</Typography>
            <PipelineOutput
              endpoint="/api/pipeline/onboard-from-github"
              body={{
                github_url: onboardForm.github_url,
                branch: onboardForm.branch,
                subdir: onboardForm.subdir || undefined,
                oci_object_prefix: onboardForm.oci_object_prefix || undefined,
                username: onboardForm.username,
                password: onboardForm.password,
              }}
              running={onboardRunning}
              onStart={() => addLogEntry('info', `Starting onboard from ${onboardForm.github_url} @ ${onboardForm.branch}`)}
              onComplete={handleOnboardComplete}
            />
          </Box>
        )}
        {onboardDialog(onboardForm, setOnboardForm, onboardDialogOpen, setOnboardDialogOpen, startOnboard, onboardFormValid)}
      </Box>
    );
  }

  const { tenant, environment, customer_name } = currentCustomer;
  // Only Publish still cares about isRepo — it targets the CURRENT env,
  // and env=repo is read-only (nothing to publish).
  const isRepo = environment === 'repo';
  const ghFormValid = Boolean(
    ghForm.github_url.startsWith('https://') &&
    ghForm.branch.trim() &&
    ghForm.username.trim() &&
    ghForm.password.trim()
  );

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <span>Tenant:</span>
          <Chip size="small" label={customer_name} color="primary" />
          <span style={{ opacity: 0.7 }}>({tenant})</span>
          <span style={{ opacity: 0.7 }}>— editing context in header:</span>
          <Chip size="small" label={environment} variant="outlined" />
          <span style={{ opacity: 0.6, fontSize: '0.8em' }}>
            (load actions target their own env; header env only affects Publish)
          </span>
        </Stack>
      </Alert>

      <Box sx={{ mb: 3 }}>{onboardButton}</Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {/* Load from OCI — always targets env=qa. Independent of header env. */}
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <CloudDownloadIcon color="primary" />
              <Typography variant="h6">Load from OCI Environment</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Downloads the merged primary + override config tree for{' '}
              <strong>{tenant}</strong> from OCI's <code>{ociSync?.bucket_name || 'internal-qa-config'}</code>{' '}
              bucket and loads it into <code>app_*</code> at{' '}
              <code>(tenant={tenant}, environment=qa)</code> via AP-data-pipeline's ap-core loaders.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Loaded rows are read-write; edits made in UCM (while browsing env=qa) can be
              published back to OCI's <code>override_configuration</code> layer via the
              Publish button below.
            </Typography>
            {ociSync?.last_bootstrap_at && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" component="div">
                  Last loaded: {formatRelative(ociSync.last_bootstrap_at)} · bucket{' '}
                  <code>{ociSync.bucket_name}</code>
                </Typography>
                {ociSync.last_publish_at && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Last published: {formatRelative(ociSync.last_publish_at)}
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
          <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
            <Button
              variant="contained"
              startIcon={ociLoadRunning ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadIcon />}
              disabled={anyFlowRunning}
              onClick={() => {
                setOciBucket(ociSync?.bucket_name || 'internal-qa-config');
                setOciDialogOpen(true);
              }}
            >
              {ociLoadRunning ? 'Loading…' : ociSync?.last_bootstrap_at ? 'Reload from OCI' : 'Load from OCI'}
            </Button>
            <Tooltip
              title={
                isRepo
                  ? 'Publish targets the CURRENT env in the header; switch away from repo to publish.'
                  : !ociSync?.last_bootstrap_at
                    ? 'Load from OCI first — Publish computes deltas against the loaded state.'
                    : ''
              }
              arrow
            >
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={publishRunning ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                  disabled={isRepo || anyFlowRunning || !ociSync?.last_bootstrap_at}
                  onClick={() => { setPublishDryRun(true); setPublishDialogOpen(true); }}
                >
                  {publishRunning ? 'Publishing…' : `Publish ${environment} to OCI…`}
                </Button>
              </span>
            </Tooltip>
          </CardActions>
        </Card>

        {/* Load from GitHub — always targets env=repo. Independent of header env. */}
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <GitHubIcon color="action" />
              <Typography variant="h6">Load from GitHub Repository</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Clones the customer's config repo and loads it into <code>app_*</code> at{' '}
              <code>(tenant={tenant}, environment=repo)</code>.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The <code>repo</code> environment is a read-only baseline for comparison and
              eventual PR generation. Loading requires GitHub credentials (username + PAT)
              entered at run time; they are not persisted.
            </Typography>
            {ghSync?.last_ingested_at && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" component="div">
                  Last loaded: {formatRelative(ghSync.last_ingested_at)}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  {ghSync.github_url}
                  {ghSync.branch ? ` @ ${ghSync.branch}` : ''}
                  {ghSync.last_ingested_sha ? ` (${ghSync.last_ingested_sha.slice(0, 7)})` : ''}
                </Typography>
              </Box>
            )}
          </CardContent>
          <CardActions sx={{ px: 2, pb: 2 }}>
            <Button
              variant="contained"
              startIcon={ghLoadRunning ? <CircularProgress size={16} color="inherit" /> : <GitHubIcon />}
              disabled={anyFlowRunning}
              onClick={() => setGhDialogOpen(true)}
            >
              {ghLoadRunning ? 'Loading…' : ghSync?.last_ingested_at ? 'Reload from GitHub' : 'Load from GitHub'}
            </Button>
          </CardActions>
        </Card>
      </Stack>

      {/* SSE output — one panel; only one flow runs at a time. activeFlow
          determines which endpoint the panel listens on. */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {activeFlow === 'oci-publish' ? 'Publish Output' : activeFlow === 'onboard' ? 'Onboarding Output' : 'Load Output'}
        </Typography>
        {activeFlow === 'onboard' ? (
          <PipelineOutput
            endpoint="/api/pipeline/onboard-from-github"
            body={{
              github_url: onboardForm.github_url,
              branch: onboardForm.branch,
              subdir: onboardForm.subdir || undefined,
              oci_object_prefix: onboardForm.oci_object_prefix || undefined,
              username: onboardForm.username,
              password: onboardForm.password,
            }}
            running={onboardRunning}
            onStart={() => addLogEntry('info', `Starting onboard from ${onboardForm.github_url} @ ${onboardForm.branch}`)}
            onComplete={handleOnboardComplete}
          />
        ) : activeFlow === 'gh-load' ? (
          <PipelineOutput
            endpoint="/api/pipeline/load-from-github"
            body={{
              github_url: ghForm.github_url,
              branch: ghForm.branch,
              subdir: ghForm.subdir || undefined,
              username: ghForm.username,
              password: ghForm.password,
            }}
            running={ghLoadRunning}
            onStart={() => addLogEntry('info', `Starting Load from GitHub for ${tenant}/repo`)}
            onComplete={handleGhLoadComplete}
          />
        ) : activeFlow === 'oci-publish' ? (
          <PipelineOutput
            endpoint="/api/pipeline/publish-to-oci"
            body={{ dryRun: publishDryRun }}
            running={publishRunning}
            onStart={() =>
              addLogEntry('info', `Starting Publish to OCI for ${tenant}/${environment}${publishDryRun ? ' (dry-run)' : ''}`)
            }
            onComplete={handlePublishComplete}
          />
        ) : (
          <PipelineOutput
            endpoint="/api/pipeline/load-from-oci"
            body={{ environment: 'qa', bucket_name: ociBucket.trim() || undefined }}
            running={ociLoadRunning}
            onStart={() => addLogEntry('info', `Starting Load from OCI for ${tenant}/qa (bucket: ${ociBucket.trim() || 'default'})`)}
            onComplete={handleOciLoadComplete}
          />
        )}
      </Box>

      {/* OCI Load confirmation dialog — bucket-name is editable per-load. */}
      <Dialog open={ociDialogOpen} onClose={() => setOciDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Load {tenant} from OCI</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Downloads primary + override configs for <strong>{tenant}</strong> from the OCI bucket and loads them into
            <code> app_*</code> at <code>(tenant={tenant}, environment=qa)</code>.
          </Typography>
          <TextField
            label="OCI Bucket Name"
            value={ociBucket}
            onChange={e => setOciBucket(e.target.value)}
            required
            fullWidth
            autoFocus
            helperText={
              ociSync?.bucket_name
                ? `Last used: ${ociSync.bucket_name}. Change to point at a different bucket for this load.`
                : `Default: internal-qa-config. Change if you need to target a different bucket.`
            }
            error={ociBucket.trim().length > 0 && !ociBucketValid}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOciDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={startOciLoad} disabled={!ociBucket.trim() || !ociBucketValid}>
            Start Load
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish confirmation dialog */}
      <Dialog open={publishDialogOpen} onClose={() => setPublishDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Publish to OCI — {tenant}/{environment}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Computes deltas between UCM's <code>({tenant}, {environment})</code> DB state and{' '}
            the primary baseline at <code>({tenant}, repo)</code>, then writes the deltas to OCI's{' '}
            <code>override_configuration</code> layer at bucket{' '}
            <code>{ociSync?.bucket_name}</code>.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>PR G scope:</strong> Views only. Models, pivots, and UI Config publishers land in
            follow-up PRs. Views that were deleted in <code>{environment}</code> but still exist in
            <code>repo</code> are surfaced as TOMBSTONE_WARN and skipped (runtime tombstone
            convention pending confirmation).
          </Alert>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={<Checkbox checked={publishDryRun} onChange={e => setPublishDryRun(e.target.checked)} />}
            label={
              <Box>
                <Typography variant="body2" component="span">Dry-run (no OCI writes)</Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  Compute + display the plan; skip the upload/delete step. Recommended for first run.
                </Typography>
              </Box>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={publishDryRun ? 'primary' : 'warning'}
            onClick={startPublish}
          >
            {publishDryRun ? 'Run Dry-Run' : 'Publish for Real'}
          </Button>
        </DialogActions>
      </Dialog>

      {onboardDialog(onboardForm, setOnboardForm, onboardDialogOpen, setOnboardDialogOpen, startOnboard, onboardFormValid)}

      {/* GitHub credentials dialog */}
      <Dialog open={ghDialogOpen} onClose={() => setGhDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Load {tenant} from GitHub</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Repository URL (HTTPS)"
              placeholder="https://github.com/s5-stratos/trd-configs.git"
              value={ghForm.github_url}
              onChange={e => setGhForm(f => ({ ...f, github_url: e.target.value }))}
              required
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Branch"
                value={ghForm.branch}
                onChange={e => setGhForm(f => ({ ...f, branch: e.target.value }))}
                required
                sx={{ flex: 1 }}
              />
              <TextField
                label="Subdirectory (optional)"
                placeholder="e.g. configuration/"
                value={ghForm.subdir}
                onChange={e => setGhForm(f => ({ ...f, subdir: e.target.value }))}
                sx={{ flex: 1 }}
              />
            </Stack>
            <TextField
              label="GitHub Username"
              value={ghForm.username}
              onChange={e => setGhForm(f => ({ ...f, username: e.target.value }))}
              required
              autoComplete="off"
              fullWidth
            />
            <TextField
              label="Personal Access Token (PAT)"
              type="password"
              value={ghForm.password}
              onChange={e => setGhForm(f => ({ ...f, password: e.target.value }))}
              required
              autoComplete="off"
              helperText="Sent once with this request; never stored. Needs 'repo' scope on the target repo."
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGhDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={startGhLoad} disabled={!ghFormValid}>
            Start Load
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerPipeline;
